import {
  captureException,
  init,
  setTag,
} from "@workspace/semparse-sdk-core/semparse-sdk-core";

type InitSemparseClientInstrumentationInput = {
  appTag: string;
};

type ClientInstrumentationState = {
  initializedApps: Set<string>;
  listenersInstalledApps: Set<string>;
  activeThemeByApp: Map<string, "dark" | "light">;
  captureWindowStartedAt: number;
  captureCount: number;
  recentCaptureKeys: Map<string, number>;
};

const DEFAULT_DIST = "web";
const STATE_KEY = "__workspaceSemparseClientInstrumentationState";

const CAPTURE_WINDOW_MS = 5000;
const CAPTURE_LIMIT_PER_WINDOW = 25;
const CAPTURE_DEDUPE_MS = 1500;

const normalizeText = (value: unknown): string => {
  if (typeof value !== "string") {
    return "";
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : "";
};

const toRecord = (value: unknown): Record<string, unknown> | undefined => {
  if (!value || typeof value !== "object") {
    return undefined;
  }

  return Object.fromEntries(Object.entries(value));
};

const readGlobalValue = (key: string): unknown => {
  const descriptor = Object.getOwnPropertyDescriptor(globalThis, key);
  if (!descriptor) {
    return undefined;
  }

  if (typeof descriptor.get === "function") {
    const value: unknown = descriptor.get.call(globalThis);
    return value;
  }

  const value: unknown = descriptor.value;
  return value;
};

const setGlobalValue = (key: string, value: unknown): void => {
  Object.defineProperty(globalThis, key, {
    value,
    configurable: true,
    enumerable: false,
    writable: true,
  });
};

const readEnv = (key: string): string => {
  const processRecord = toRecord(readGlobalValue("process"));
  if (!processRecord) {
    return "";
  }

  const envRecord = toRecord(processRecord.env);
  if (!envRecord) {
    return "";
  }

  return normalizeText(envRecord[key]);
};

const resolveDsn = (): string => {
  return readEnv("NEXT_PUBLIC_SEMPARSE_DSN");
};

const resolveStage = (): string => {
  return readEnv("NEXT_PUBLIC_STAGE") || readEnv("STAGE") || "development";
};

const resolveRelease = (): string | undefined => {
  const release = readEnv("NEXT_PUBLIC_VERSION") || readEnv("VERSION");
  return release.length > 0 ? release : undefined;
};

const resolveDist = (): string => {
  return readEnv("NEXT_PUBLIC_SEMPARSE_DIST") || DEFAULT_DIST;
};

const resolveTheme = (): "dark" | "light" => {
  if (typeof globalThis.matchMedia !== "function") {
    return "light";
  }

  const mediaQuery = globalThis.matchMedia("(prefers-color-scheme: dark)");
  return mediaQuery.matches ? "dark" : "light";
};

const setThemeTag = (input: {
  appTag: string;
  state: ClientInstrumentationState;
}): void => {
  const nextTheme = resolveTheme();
  const activeTheme = input.state.activeThemeByApp.get(input.appTag);
  if (nextTheme === activeTheme) {
    return;
  }

  input.state.activeThemeByApp.set(input.appTag, nextTheme);
  setTag({ key: "theme", value: nextTheme });
};

const toError = (input: { raw: unknown; fallbackMessage: string }): Error => {
  if (input.raw instanceof Error) {
    return input.raw;
  }

  if (typeof input.raw === "string" && input.raw.length > 0) {
    return new Error(input.raw);
  }

  return new Error(input.fallbackMessage);
};

const isClientInstrumentationState = (
  value: unknown,
): value is ClientInstrumentationState => {
  const record = toRecord(value);
  return Boolean(
    record &&
      record.initializedApps instanceof Set &&
      record.listenersInstalledApps instanceof Set &&
      record.activeThemeByApp instanceof Map &&
      typeof record.captureWindowStartedAt === "number" &&
      typeof record.captureCount === "number" &&
      record.recentCaptureKeys instanceof Map,
  );
};

const getState = (): ClientInstrumentationState => {
  const existing = readGlobalValue(STATE_KEY);
  if (isClientInstrumentationState(existing)) {
    return existing;
  }

  const initial: ClientInstrumentationState = {
    initializedApps: new Set(),
    listenersInstalledApps: new Set(),
    activeThemeByApp: new Map(),
    captureWindowStartedAt: 0,
    captureCount: 0,
    recentCaptureKeys: new Map(),
  };

  setGlobalValue(STATE_KEY, initial);
  return initial;
};

const shouldCapture = (input: {
  error: Error;
  state: ClientInstrumentationState;
}): boolean => {
  const now = Date.now();
  if (now - input.state.captureWindowStartedAt > CAPTURE_WINDOW_MS) {
    input.state.captureWindowStartedAt = now;
    input.state.captureCount = 0;
  }

  for (const [key, timestamp] of input.state.recentCaptureKeys.entries()) {
    if (now - timestamp > CAPTURE_WINDOW_MS) {
      input.state.recentCaptureKeys.delete(key);
    }
  }

  if (input.state.captureCount >= CAPTURE_LIMIT_PER_WINDOW) {
    return false;
  }

  const dedupeKey = `${input.error.name}:${input.error.message}:${input.error.stack ?? ""}`;
  const lastSeen = input.state.recentCaptureKeys.get(dedupeKey);
  if (typeof lastSeen === "number" && now - lastSeen < CAPTURE_DEDUPE_MS) {
    return false;
  }

  input.state.recentCaptureKeys.set(dedupeKey, now);
  input.state.captureCount += 1;
  return true;
};

const captureClientError = (input: {
  appTag: string;
  error: Error;
  state: ClientInstrumentationState;
  mechanism: string;
}): void => {
  if (!shouldCapture({ error: input.error, state: input.state })) {
    return;
  }

  const stage = resolveStage();

  void captureException({
    error: input.error,
    level: "error",
    tags: {
      app: input.appTag,
      stage,
      runtime: "nextjs-browser",
      mechanism: input.mechanism,
    },
    contexts: {
      browser: {
        url: globalThis.location.href,
      },
    },
  }).catch(() => {});
};

export const initSemparseClientInstrumentation = (
  input: InitSemparseClientInstrumentationInput,
): void => {
  if (globalThis.window === undefined) {
    return;
  }

  const dsn = resolveDsn();
  if (dsn.length === 0) {
    return;
  }

  const state = getState();
  if (!state.initializedApps.has(input.appTag)) {
    const stage = resolveStage();

    init({
      dsn,
      release: resolveRelease(),
      dist: resolveDist(),
      environment: stage,
      platform: "web",
      defaultTags: {
        app: input.appTag,
        stage,
        runtime: "nextjs-browser",
      },
    });

    state.initializedApps.add(input.appTag);
    setThemeTag({ appTag: input.appTag, state });
  }

  if (state.listenersInstalledApps.has(input.appTag)) {
    return;
  }

  const onError = (event: ErrorEvent): void => {
    if (!event.error && event.message === "Script error.") {
      return;
    }

    captureClientError({
      appTag: input.appTag,
      state,
      error: toError({
        raw: event.error,
        fallbackMessage: event.message || "window_error",
      }),
      mechanism: "window.onerror",
    });
  };

  const onUnhandledRejection = (event: PromiseRejectionEvent): void => {
    captureClientError({
      appTag: input.appTag,
      state,
      error: toError({
        raw: event.reason,
        fallbackMessage: "unhandled_promise_rejection",
      }),
      mechanism: "window.unhandledrejection",
    });
  };

  const mediaQuery =
    typeof globalThis.matchMedia === "function"
      ? globalThis.matchMedia("(prefers-color-scheme: dark)")
      : undefined;

  const onThemeChange = (): void => {
    setThemeTag({ appTag: input.appTag, state });
  };

  globalThis.addEventListener("error", onError);
  globalThis.addEventListener("unhandledrejection", onUnhandledRejection);
  mediaQuery?.addEventListener("change", onThemeChange);

  state.listenersInstalledApps.add(input.appTag);
};
